import { useCallback, useEffect, useState } from 'react';
import type { RefObject } from 'react';

import type { PortalPanelBox } from '../utils/portalDropdownGeometry';

export type PortalBoxComputer = (
  triggerRect: DOMRectReadOnly,
  viewportWidth: number,
  viewportHeight: number,
) => PortalPanelBox;

export const usePortalDropdownPosition = (
  open: boolean,
  triggerRef: RefObject<HTMLElement | null>,
  computeBox: PortalBoxComputer,
): PortalPanelBox | null => {
  const [panelBox, setPanelBox] = useState<PortalPanelBox | null>(null);

  const reposition = useCallback(() => {
    const el = triggerRef.current;
    if (!el || typeof window === 'undefined') {
      setPanelBox(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setPanelBox(computeBox(rect, window.innerWidth, window.innerHeight));
  }, [triggerRef, computeBox]);

  useEffect(() => {
    if (!open) {
      setPanelBox(null);
      return undefined;
    }
    reposition();
    const timer = window.setTimeout(() => {
      reposition();
    }, 0);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, reposition]);

  return panelBox;
};
